/**
 * @fileoverview Modal for marking a violation occurrence as repaired.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Capture repair notes from inspector
 * - Confirm repair action with timestamp and actor
 * - Support undo/cancel before commit
 *
 * Dependencies:
 * - LoggedViolationOccurrence from ./types
 * - Lucide icons
 *
 * Architecture:
 * - Presentational component — receives occurrence data and callbacks
 * - Parent component handles persistence to Dexie
 * - Pure presentation — no Dexie, no fetch
 *
 * Notes:
 * - Used by OccurrenceRepairBlock and any surface that needs repair workflow
 * - Shared across tenant-web, mobile-pwa, and inspection-renderer surfaces
 */

import { useState, useEffect } from 'react';
import { CheckCircle2, X, Wrench } from 'lucide-react';
import type { LoggedViolationOccurrence } from './types';

/**
 * @contract OccurrenceRepairData
 *
 * Purpose:
 * - Data returned when user confirms a repair
 *
 * Fields:
 * - repairNotes: Optional notes describing how the violation was corrected
 * - fixedAt: ISO timestamp when repair was confirmed
 * - fixedBy: User ID who confirmed the repair (passed in by caller)
 */
export interface OccurrenceRepairData {
  /** Notes describing how the violation was corrected */
  repairNotes: string;
  /** ISO timestamp when repair was confirmed */
  fixedAt: string;
}

/**
 * @contract OccurrenceRepairModalProps
 *
 * Purpose:
 * - Props for the repair confirmation modal
 *
 * Fields:
 * - occurrenceLabel: Display label for the occurrence (e.g. "Main corridor")
 * - currentUserId: User ID to record as fixedBy
 * - userOptions: Optional list of users for a dropdown (future)
 * - onConfirm: Called with repair data when user confirms
 * - onCancel: Called when user cancels or closes modal
 * - isSubmitting: Whether the repair action is in progress
 */
export interface OccurrenceRepairModalProps {
  /** Display label for the occurrence being repaired */
  occurrenceLabel?: string;
  /** Violation code for display context */
  violationCode?: string;
  /** Violation description for display context */
  violationDescription?: string;
  /** The occurrence being repaired (for displaying details) */
  occurrence?: LoggedViolationOccurrence;
  /** Called with repair data when user confirms */
  onConfirm: (data: OccurrenceRepairData) => void | Promise<void>;
  /** Called when user cancels */
  onCancel: () => void;
  /** Whether the action is in progress */
  isSubmitting?: boolean;
}

/**
 * @component OccurrenceRepairModal
 *
 * Purpose:
 * - Modal dialog for confirming a violation occurrence repair
 *
 * Props:
 * - occurrenceLabel: Label for the occurrence
 * - violationCode: Code string for display
 * - violationDescription: Optional description for display
 * - occurrence: Optional occurrence for displaying details
 * - onConfirm: Callback with repair data
 * - onCancel: Cancel callback
 * - isSubmitting: Loading state
 *
 * Behavior:
 * - Displays occurrence details (location, original notes)
 * - Captures repair notes (optional but encouraged)
 * - Escape key closes modal
 * - Click outside closes modal
 *
 * Data Source:
 * - Props only (presentational)
 *
 * Constraints:
 * - No direct API calls — parent handles persistence
 */
export function OccurrenceRepairModal({
  occurrenceLabel,
  violationCode,
  violationDescription,
  occurrence,
  onConfirm,
  onCancel,
  isSubmitting = false,
}: OccurrenceRepairModalProps) {
  const [repairNotes, setRepairNotes] = useState('');

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel, isSubmitting]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    await onConfirm({
      repairNotes: repairNotes.trim(),
      fixedAt: new Date().toISOString(),
    });
  };

  const displayLabel = occurrenceLabel || occurrence?.location || 'This occurrence';

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => !isSubmitting && onCancel()}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center gap-2 bg-green-50">
          <Wrench className="h-5 w-5 shrink-0 text-green-600" />
          <h3 className="font-semibold text-green-900">Mark as Repaired</h3>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="ml-auto text-green-400 hover:text-green-600 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Violation context */}
          <div className="bg-gray-50 rounded-md p-3 space-y-1">
            <div className="flex items-center gap-2">
              {violationCode && (
                <span className="text-xs font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-sm">
                  {violationCode}
                </span>
              )}
              {violationDescription && (
                <span className="text-xs text-gray-600 truncate">
                  {violationDescription}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              <span className="font-medium">Location:</span> {displayLabel}
            </div>
            {occurrence?.notes && (
              <div className="text-xs text-gray-500">
                <span className="font-medium">Original notes:</span> {occurrence.notes}
              </div>
            )}
          </div>

          {/* Repair notes input */}
          <div>
            <label
              htmlFor="repair-notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Repair Notes
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <textarea
              id="repair-notes"
              value={repairNotes}
              onChange={(e) => setRepairNotes(e.target.value)}
              placeholder="Describe how the violation was corrected..."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
              disabled={isSubmitting}
            />
          </div>

          {/* Info note */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="text-xs text-blue-800 flex items-start gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <div>
                This will mark the occurrence as <strong>resolved</strong> and record
                the current timestamp and your user ID.
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-3 py-2 text-sm border rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-3 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              <CheckCircle2 size={16} />
              {isSubmitting ? 'Saving...' : 'Confirm Repair'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OccurrenceRepairModal;
